/**
 * Coordinate Conversion Math for PDF Field Placement
 * 
 * This module handles conversion between three coordinate systems:
 * 1. SCREEN PIXELS (0, 0 at top-left) - what the user sees
 * 2. RELATIVE UNITS (0-1) - PDF-independent, device-independent storage
 * 3. PDF POINTS (0, 0 at bottom-left) - what pdf-lib uses
 * 
 * The key insight: Store everything in relative units (0-1) to ensure
 * field positions remain consistent across all screen sizes.
 */

/**
 * Convert screen pixel coordinates to relative units (0-1)
 * 
 * @param {number} screenX - X coordinate in pixels (relative to container)
 * @param {number} screenY - Y coordinate in pixels (relative to container)
 * @param {number} containerWidth - Width of PDF container in pixels
 * @param {number} containerHeight - Height of PDF container in pixels
 * @returns {{x: number, y: number}} - Relative coordinates (0-1)
 */
export const screenToRelative = (
  screenX,
  screenY,
  containerWidth,
  containerHeight
) => {
  return {
    x: screenX / containerWidth,
    y: screenY / containerHeight,
  };
};

/**
 * Convert relative coordinates (0-1) back to screen pixels
 * 
 * @param {number} relX - Relative X (0-1)
 * @param {number} relY - Relative Y (0-1)
 * @param {number} containerWidth - Width of PDF container in pixels
 * @param {number} containerHeight - Height of PDF container in pixels
 * @returns {{x: number, y: number}} - Screen pixel coordinates
 */
export const relativeToScreen = (relX, relY, containerWidth, containerHeight) => {
  return {
    x: relX * containerWidth,
    y: relY * containerHeight,
  };
};

/**
 * Convert relative coordinates to PDF points
 * 
 * PDF coordinate system:
 * - Origin (0,0) is at BOTTOM-LEFT
 * - X increases to the right
 * - Y increases upward
 * 
 * Relative system:
 * - Origin (0,0) is at TOP-LEFT
 * - X increases to the right
 * - Y increases downward
 * 
 * @param {number} relX - Relative X (0-1)
 * @param {number} relY - Relative Y (0-1)
 * @param {number} pdfWidth - PDF width in points (usually 595 for A4 width)
 * @param {number} pdfHeight - PDF height in points (usually 842 for A4 height)
 * @returns {{x: number, y: number}} - PDF coordinates in points
 */
export const relativeToPdf = (relX, relY, pdfWidth, pdfHeight) => {
  const pdfX = relX * pdfWidth;
  // Y must be flipped: PDF uses bottom-left origin, relative uses top-left
  const pdfY = (1 - relY) * pdfHeight;

  return {
    x: pdfX,
    y: pdfY,
  };
};

/**
 * Convert PDF points back to relative coordinates
 * 
 * @param {number} pdfX - PDF X coordinate in points
 * @param {number} pdfY - PDF Y coordinate in points
 * @param {number} pdfWidth - PDF width in points
 * @param {number} pdfHeight - PDF height in points
 * @returns {{x: number, y: number}} - Relative coordinates (0-1)
 */
export const pdfToRelative = (pdfX, pdfY, pdfWidth, pdfHeight) => {
  const relX = pdfX / pdfWidth;
  const relY = (1 - pdfY / pdfHeight);

  return {
    x: relX,
    y: relY,
  };
};

/**
 * Convert sizes from relative units to screen pixels
 * 
 * @param {number} relWidth - Relative width (0-1)
 * @param {number} relHeight - Relative height (0-1)
 * @param {number} containerWidth - Container width in pixels
 * @param {number} containerHeight - Container height in pixels
 * @returns {{width: number, height: number}} - Size in screen pixels
 */
export const relativeSizeToScreen = (
  relWidth,
  relHeight,
  containerWidth,
  containerHeight
) => {
  return {
    width: relWidth * containerWidth,
    height: relHeight * containerHeight,
  };
};

/**
 * Convert sizes from screen pixels to relative units
 * 
 * @param {number} screenWidth - Width in pixels
 * @param {number} screenHeight - Height in pixels
 * @param {number} containerWidth - Container width in pixels
 * @param {number} containerHeight - Container height in pixels
 * @returns {{width: number, height: number}} - Size in relative units (0-1)
 */
export const screenSizeToRelative = (
  screenWidth,
  screenHeight,
  containerWidth,
  containerHeight
) => {
  return {
    width: screenWidth / containerWidth,
    height: screenHeight / containerHeight,
  };
};

/**
 * Convert sizes from relative units to PDF points
 * 
 * @param {number} relWidth - Relative width (0-1)
 * @param {number} relHeight - Relative height (0-1)
 * @param {number} pdfWidth - PDF width in points
 * @param {number} pdfHeight - PDF height in points
 * @returns {{width: number, height: number}} - Size in PDF points
 */
export const relativeSizeToPdf = (relWidth, relHeight, pdfWidth, pdfHeight) => {
  return {
    width: relWidth * pdfWidth,
    height: relHeight * pdfHeight,
  };
};

/**
 * Maintain aspect ratio when resizing
 * 
 * @param {number} originalWidth - Original width
 * @param {number} originalHeight - Original height
 * @param {number} newDimension - New width or height
 * @param {boolean} isWidth - true if newDimension is width, false if height
 * @returns {{width: number, height: number}} - Resized dimensions maintaining aspect ratio
 */
export const maintainAspectRatio = (
  originalWidth,
  originalHeight,
  newDimension,
  isWidth = true
) => {
  const aspectRatio = originalWidth / originalHeight;

  if (isWidth) {
    return {
      width: newDimension,
      height: newDimension / aspectRatio,
    };
  } else {
    return {
      width: newDimension * aspectRatio,
      height: newDimension,
    };
  }
};

/**
 * Get standard PDF dimensions in points (72 points = 1 inch)
 * 
 * A4: 210mm × 297mm = 595pt × 842pt
 * Letter: 216mm × 279mm = 612pt × 792pt
 * 
 * @param {string} format - 'A4' or 'LETTER'
 * @returns {{width: number, height: number}} - PDF dimensions in points
 */
export const getPdfDimensions = (format = 'A4') => {
  const dimensions = {
    A4: { width: 595, height: 842 },
    LETTER: { width: 612, height: 792 },
  };

  return dimensions[format] || dimensions.A4;
};

/**
 * Calculate optimal container size based on PDF aspect ratio and screen size
 * 
 * @param {number} pdfWidth - PDF width in points
 * @param {number} pdfHeight - PDF height in points
 * @param {number} maxScreenWidth - Maximum screen width available
 * @param {number} maxScreenHeight - Maximum screen height available
 * @param {number} scale - Scale factor (e.g., 1.0 for 100%, 0.8 for 80%)
 * @returns {{width: number, height: number}} - Optimal container size
 */
export const calculateOptimalContainerSize = (
  pdfWidth,
  pdfHeight,
  maxScreenWidth,
  maxScreenHeight,
  scale = 1.0
) => {
  const pdfAspectRatio = pdfWidth / pdfHeight;

  let width = maxScreenWidth * scale;
  let height = width / pdfAspectRatio;

  if (height > maxScreenHeight * scale) {
    height = maxScreenHeight * scale;
    width = height * pdfAspectRatio;
  }

  return { width: Math.floor(width), height: Math.floor(height) };
};
